import {
  COLOURS,
  INSTRUMENTS,
  layoutDiagram,
  type NeckDiagram,
  type NeckDiagramSheet,
} from "@wmf/shared/neckDiagrams";
import {
  useRef,
  useId,
  useEffect,
  useContext,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useState } from "react";
import "../neckDiagrams.css";
import { NeckDiagramStudentContext } from "./NeckDiagramViewing.js";
import { NeckDiagramPlayback, useDiagramPlayer } from "./NeckDiagramPlayback.js";

export function NeckDiagramView({
  diagram,
  interactive,
  selected = [],
  onCell,
  onMark,
  onKeyDown,
}: {
  diagram: NeckDiagram;
  interactive?: boolean;
  selected?: string[];
  onCell?: (string: number, fret: number) => void;
  onMark?: (string: number) => void;
  onKeyDown?: (event: KeyboardEvent<SVGSVGElement>) => void;
}) {
  const view = useContext(NeckDiagramStudentContext);
  const player = useDiagramPlayer(diagram);
  useEffect(view.activate, [view.activate]);
  const layout = layoutDiagram(diagram, { leftHanded: view.leftHanded });
  const [audition, setAudition] = useState<string | null>(null);
  const noteElements = useRef(new Map<string, SVGRectElement>());
  const noteHint = useId();
  const noteKey = (cell: { string: number; fret: number }) => `${cell.string}:${cell.fret}`;
  const notes = layout.cells.filter(cell => layout.dots.some(dot => dot.string === cell.string && dot.fret === cell.fret));
  const activeNote = notes.some(note => noteKey(note) === audition) ? audition : notes[0] ? noteKey(notes[0]) : null;
  function noteKeyboard(event: KeyboardEvent<SVGRectElement>, cell: (typeof notes)[number]) {
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); void player.play(cell); return; }
    let next = event.key === "Home" ? notes[0] : event.key === "End" ? notes.at(-1) : undefined;
    if (event.key.startsWith("Arrow")) {
      const horizontal = event.key === "ArrowLeft" || event.key === "ArrowRight";
      const direction = event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1;
      const primary = (note: (typeof notes)[number]) => direction * (horizontal ? note.x - cell.x : note.y - cell.y);
      const secondary = (note: (typeof notes)[number]) => Math.abs(horizontal ? note.y - cell.y : note.x - cell.x);
      // Follow the displayed positions, including left-handed boards; prefer
      // the same row/column before the nearest note in an adjacent one.
      next = notes.filter(note => primary(note) > 0).sort((a, b) => secondary(a) - secondary(b) || primary(a) - primary(b))[0];
    } else if (!next) return;
    event.preventDefault();
    if (next) { const key = noteKey(next); setAudition(key); noteElements.current.get(key)?.focus(); }
  }
  // Never guess string order while a student's saved handedness is unknown.
  // Keep their chart hidden on loading/failure, with a retry that preserves it.
  if (view.pending) return <p role="status">Loading student’s chart view…</p>;
  if (view.error) return <div><p role="alert" className="error">Could not load the student’s chart view.</p><button type="button" onClick={view.retry}>Try again</button></div>;
  return (
    <figure
      className={`neck-diagram ${diagram.kind !== "chord" ? "neck-diagram-horizontal" : ""} ${interactive ? "neck-diagram-interactive" : ""}`}
      style={{ "--neck-diagram-width": `${layout.width}px` } as CSSProperties}
    >
      <figcaption>
        <strong>{diagram.title || "Untitled diagram"}</strong>
        <span>
          {INSTRUMENTS[diagram.instrument].name} ·{" "}
          {diagram.tuningName || "Custom tuning"} · {diagram.tuning.join(" ")}
        </span>
        {view.leftHanded && <span className="neck-handedness-label">Left-handed view</span>}
      </figcaption>
      <NeckDiagramPlayback player={player} chord={diagram.kind === "chord"} />
      {player.enabled && !interactive && notes.length > 0 && <p id={noteHint} className="visually-hidden">Use arrow keys to choose a note, Enter or Space to hear it, and Tab to leave the chart.</p>}
      <div className="neck-diagram-scroll">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          style={
            interactive
              ? { width: layout.width, minWidth: layout.width }
              : undefined
          }
          role={interactive || player.enabled ? "group" : "img"}
          aria-label={`${diagram.title || "Neck diagram"}, ${INSTRUMENTS[diagram.instrument].name}`}
          tabIndex={interactive ? 0 : undefined}
          aria-describedby={player.enabled && !interactive && notes.length > 0 ? noteHint : undefined}
          onKeyDown={onKeyDown}
        >
          <title>{diagram.title || "Neck diagram"}</title>
          {layout.inlays.map((p, i) => (
            <circle key={`i${i}`} cx={p.x} cy={p.y} r={5} fill="#ddd" />
          ))}
          {layout.lines.map((line, i) => (
            <line
              key={`l${i}`}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke="#444"
              strokeWidth={line.nut ? 5 : 1}
            />
          ))}
          {layout.fretLabels
            .filter(
              (_, i) =>
                diagram.kind !== "chord" || (i === 0 && layout.positionLabel),
            )
            .map((p) => (
              <text
                key={`f${p.fret}`}
                x={p.x}
                y={p.y}
                textAnchor="middle"
                fontSize="14"
              >
                {diagram.kind === "chord" ? layout.positionLabel : p.fret}
              </text>
            ))}
          {layout.barres.map((barre, i) => (
            <line
              key={`b${i}`}
              x1={barre.from.x}
              y1={barre.from.y}
              x2={barre.to.x}
              y2={barre.to.y}
              stroke="#333"
              strokeWidth="22"
              strokeLinecap="round"
            />
          ))}
          {layout.dots.map((dot) => {
            const colour = COLOURS[dot.colour ?? "black"];
            const common = {
              fill: dot.hollow ? "white" : colour,
              stroke: colour,
              strokeWidth: 2,
            };
            const shape: ReactNode =
              dot.shape === "square" ? (
                <rect x={-16} y={-16} width={32} height={32} {...common} />
              ) : dot.shape === "diamond" ? (
                <polygon points="0,-20 20,0 0,20 -20,0" {...common} />
              ) : dot.shape === "triangle" ? (
                <polygon points="0,-20 19,16 -19,16" {...common} />
              ) : (
                <circle r={17} {...common} />
              );
            return (
              <g
                key={`${dot.string}:${dot.fret}`}
                transform={`translate(${dot.x} ${dot.y})`}
              >
                <title>{`String ${dot.string + 1}, fret ${dot.fret}${dot.label ? `, ${dot.label}` : ""}`}</title>
                {shape}
                <text
                  textAnchor="middle"
                  dy=".35em"
                  fill={dot.hollow ? colour : "white"}
                  fontSize="13"
                  fontWeight="bold"
                >
                  {dot.label}
                </text>
              </g>
            );
          })}
          {layout.marks
            .filter((p) => diagram.kind === "chord" || p.mark !== null)
            .map((p) => (
              <g key={`m${p.string}`}>
                <text x={p.x} y={p.y + 6} textAnchor="middle" fontSize="24">
                  {p.mark === "open" ? "○" : p.mark === "muted" ? "×" : ""}
                </text>
                {interactive && diagram.kind === "chord" && (
                  <rect
                    x={p.x - 22}
                    y={p.y - 22}
                    width="44"
                    height="44"
                    fill="transparent"
                    role="button"
                    tabIndex={0}
                    aria-label={`String ${p.string + 1}: ${p.mark ?? "unmarked"}`}
                    onClick={() => onMark?.(p.string)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        onMark?.(p.string);
                      }
                    }}
                  />
                )}
              </g>
            ))}
          {(interactive || player.enabled) &&
            layout.cells
              .filter((p) => interactive ? diagram.kind !== "chord" || p.fret > 0 : layout.dots.some(d => d.string === p.string && d.fret === p.fret))
              .map((p) => (
                <rect
                  key={`c${p.string}:${p.fret}`}
                  x={p.x - 22}
                  y={p.y - 22}
                  width="44"
                  height="44"
                  fill="transparent"
                  stroke={
                    selected.includes(`${p.string}:${p.fret}`)
                      ? "#1372c3"
                      : "none"
                  }
                  strokeWidth="3"
                  role="button"
                  aria-label={`String ${p.string + 1}, fret ${p.fret}`}
                  ref={interactive ? undefined : node => { const key = noteKey(p); if (node) noteElements.current.set(key, node); else noteElements.current.delete(key); }}
                  tabIndex={interactive ? undefined : noteKey(p) === activeNote ? 0 : -1}
                  onFocus={interactive ? undefined : () => setAudition(noteKey(p))}
                  onClick={() => { if (!interactive) setAudition(noteKey(p)); onCell?.(p.string, p.fret); if (diagram.dots.some(d => d.string === p.string && d.fret === p.fret)) void player.play(p); }}
                  onKeyDown={interactive ? undefined : event => noteKeyboard(event, p)}
                />
              ))}
        </svg>
      </div>
    </figure>
  );
}
export function NeckDiagramSheetView({ sheet }: { sheet: NeckDiagramSheet }) {
  return (
    <article className="neck-sheet">
      <h3>{sheet.title}</h3>
      {sheet.notes && <p>{sheet.notes}</p>}
      <div className="neck-sheet-grid">
        {sheet.diagrams.map((diagram) => (
          <NeckDiagramView key={diagram.id} diagram={diagram} />
        ))}
      </div>
    </article>
  );
}
export function NeckDiagramSheets({
  sheets,
}: {
  sheets: readonly { id: string; document: NeckDiagramSheet }[];
}) {
  const [printing, setPrinting] = useState<NeckDiagramSheet | null>(null);
  const pending = useRef(false);
  function print(sheet: NeckDiagramSheet) {
    if (pending.current) return;
    pending.current = true;
    setPrinting(sheet);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        document.body.classList.add("printing-neck-sheet");
        const cleanup = () => {
          document.body.classList.remove("printing-neck-sheet");
          setPrinting(null);
          pending.current = false;
        };
        window.addEventListener("afterprint", cleanup, { once: true });
        try {
          window.print();
        } catch {
          window.removeEventListener("afterprint", cleanup);
          cleanup();
        }
      }),
    );
  }
  const columns =
    printing && printing.diagrams.length > 12
      ? 4
      : printing && printing.diagrams.length > 4
        ? 3
        : 2;
  const rows = Math.ceil((printing?.diagrams.length ?? 1) / columns);
  const printStyle = {
    "--neck-print-columns": columns,
    "--neck-print-height": `${Math.floor(210 / rows) - 10}mm`,
  } as CSSProperties;
  return (
    <>
      {sheets.map((sheet) => (
        <section key={sheet.id} className="neck-sheet-attachment">
          <NeckDiagramSheetView sheet={sheet.document} />
          <button
            type="button"
            className="secondary"
            onClick={() => print(sheet.document)}
          >
            Print / download PDF
          </button>
        </section>
      ))}
      {printing &&
        createPortal(
          <div className="neck-print-root" style={printStyle}>
            <NeckDiagramSheetView sheet={printing} />
          </div>,
          document.body,
        )}
    </>
  );
}
